/**
 * ShopInfoDrawer — slide-up drawer with shop contact details + chat entry.
 * Fetches from /api/shops/public/{shopId} (no auth).
 */

import { useEffect, useState, type CSSProperties } from 'react'
import { apiUrl, authFetch } from '@/config'

interface ShopInfo {
  id: string
  name?: string
  description?: string
  phone?: string
  whatsapp?: string
  email?: string
  openingHours?: string
  address?: string
}

interface Props {
  shopId: string
  onClose: () => void
  onChat: () => void
}

export function ShopInfoDrawer({ shopId, onClose, onChat }: Props) {
  const [info, setInfo] = useState<ShopInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    authFetch(apiUrl(`/api/shops/public/${encodeURIComponent(shopId)}`))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setInfo(d) })
      .catch(() => { if (!cancelled) setInfo(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [shopId])

  const waNum = info?.whatsapp?.replace(/[^0-9]/g, '') || ''

  // Replace ISO timestamps inside an opening-hours string with HH:MM local time.
  const formatHours = (raw: string): string => raw.replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z?/g,
    (iso) => {
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
  )

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 480,
          background: '#111', color: '#fff',
          borderRadius: 16,
          padding: 20,
          boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
          maxHeight: '85vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>{loading ? 'Loading…' : (info?.name || 'Shop info')}</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: 4 }}
            title="Close"
          >✕</button>
        </div>

        {loading ? null : !info ? (
          <p style={{ opacity: 0.7 }}>Could not load shop info.</p>
        ) : (
          <>
            {info.description && <p style={{ opacity: 0.8, marginTop: 0 }}>{info.description}</p>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {info.phone && (
                <a href={`tel:${info.phone}`} style={rowStyle}>
                  <span>📞</span><span>{info.phone}</span>
                </a>
              )}
              {info.whatsapp && (
                <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" style={rowStyle}>
                  <span>💬</span><span>WhatsApp: {info.whatsapp}</span>
                </a>
              )}
              {info.email && (
                <a href={`mailto:${info.email}`} style={rowStyle}>
                  <span>✉️</span><span>{info.email}</span>
                </a>
              )}
              {info.openingHours && (
                <div style={rowStyle}>
                  <span>🕒</span><span style={{ whiteSpace: 'pre-line' }}>{formatHours(info.openingHours)}</span>
                </div>
              )}
              {info.address && (
                <div style={rowStyle}>
                  <span>📍</span><span>{info.address}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => { onChat(); onClose() }}
              style={{
                marginTop: 18, width: '100%',
                background: '#10b981', color: '#fff',
                border: 'none', borderRadius: 10,
                padding: '12px 16px', fontSize: 15, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              💬 Chat with owner
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const rowStyle: CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 10,
  padding: '10px 12px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  color: '#fff', textDecoration: 'none',
  fontSize: 14,
}
